export const LEAD_ATTRIBUTION_STORAGE_KEY = "bytelogic:lead-attribution";

const TTL_MS = 90 * 24 * 60 * 60 * 1000;

const TRACKING_PARAM_KEYS = [
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type LeadAttributionTrackingKey = (typeof TRACKING_PARAM_KEYS)[number];

export type LeadAttribution = {
  gclid: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  landing_page_url: string;
};

type LeadAttributionStored = LeadAttribution & {
  savedAt: string;
  expiresAt: string;
};

const emptyAttribution = (): LeadAttribution => ({
  gclid: "",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_term: "",
  utm_content: "",
  landing_page_url: "",
});

const readRaw = (): LeadAttributionStored | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LEAD_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LeadAttributionStored;
    if (!parsed?.expiresAt) return null;
    if (Date.parse(parsed.expiresAt) <= Date.now()) {
      localStorage.removeItem(LEAD_ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeAttribution = (values: LeadAttribution) => {
  if (typeof window === "undefined") return;
  const now = Date.now();
  const payload: LeadAttributionStored = {
    ...values,
    savedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + TTL_MS).toISOString(),
  };
  try {
    localStorage.setItem(LEAD_ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore quota / private mode failures.
  }
};

const hasAnyTrackingParam = (params: URLSearchParams) =>
  TRACKING_PARAM_KEYS.some((key) => Boolean(params.get(key)?.trim()));

/**
 * If the current URL has gclid/utm_* params, overwrite stored attribution
 * (90-day TTL). If the URL has none, leave existing storage untouched.
 */
export const captureLeadAttributionFromUrl = (
  href: string = typeof window !== "undefined" ? window.location.href : ""
): LeadAttribution | null => {
  if (typeof window === "undefined" || !href) return readLeadAttribution();

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return readLeadAttribution();
  }

  if (!hasAnyTrackingParam(url.searchParams)) {
    return readLeadAttribution();
  }

  const next = emptyAttribution();
  for (const key of TRACKING_PARAM_KEYS) {
    next[key] = url.searchParams.get(key)?.trim() || "";
  }
  next.landing_page_url = url.toString();
  writeAttribution(next);
  return next;
};

/** Non-empty stored attribution for form / API submit (excludes expiry metadata). */
export const readLeadAttribution = (): LeadAttribution | null => {
  const stored = readRaw();
  if (!stored) return null;

  const values: LeadAttribution = {
    gclid: String(stored.gclid ?? "").trim(),
    utm_source: String(stored.utm_source ?? "").trim(),
    utm_medium: String(stored.utm_medium ?? "").trim(),
    utm_campaign: String(stored.utm_campaign ?? "").trim(),
    utm_term: String(stored.utm_term ?? "").trim(),
    utm_content: String(stored.utm_content ?? "").trim(),
    landing_page_url: String(stored.landing_page_url ?? "").trim(),
  };

  const hasValue = Object.values(values).some(Boolean);
  return hasValue ? values : null;
};

/** Payload-ready object (omit empty strings). */
export const getLeadAttributionPayload = (): Partial<LeadAttribution> | undefined => {
  const values = readLeadAttribution();
  if (!values) return undefined;
  const payload: Partial<LeadAttribution> = {};
  (Object.keys(values) as (keyof LeadAttribution)[]).forEach((key) => {
    if (values[key]) payload[key] = values[key];
  });
  return Object.keys(payload).length ? payload : undefined;
};
